import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, MailOpen } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getAllMessages, getReadMessageIds } from '../lib/supabase';
import { Message } from '../types';

export function Messages() {
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [readIds, setReadIds] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function load() {
      if (!user?.id) return;
      const [msgs, reads] = await Promise.all([
        getAllMessages(),
        getReadMessageIds(user.id),
      ]);
      setMessages(msgs);
      setReadIds(reads);
      setIsLoading(false);
    }
    load();
  }, [user?.id]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-AT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
        Laden...
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', paddingBottom: '100px' }}>
      <Link
        to="/"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: '#9fa8da',
          textDecoration: 'none',
          marginBottom: '16px',
        }}
      >
        <ArrowLeft size={18} />
        Zurueck
      </Link>

      <h1 style={{ color: '#ffd54f', fontSize: '22px', marginBottom: '4px' }}>
        Nachrichten
      </h1>
      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginBottom: '20px' }}>
        Alle Neuigkeiten und Updates
      </p>

      {messages.length === 0 && (
        <div style={{
          textAlign: 'center',
          color: 'rgba(255,255,255,0.4)',
          marginTop: '40px',
          fontSize: '15px',
        }}>
          Keine Nachrichten vorhanden.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {messages.map(msg => {
          const isRead = readIds.includes(msg.id);
          const isExpanded = expanded === msg.id;

          return (
            <div
              key={msg.id}
              style={{
                background: isRead
                  ? 'rgba(255,255,255,0.03)'
                  : 'rgba(255, 213, 79, 0.08)',
                border: isRead
                  ? '1px solid rgba(255,255,255,0.08)'
                  : '1px solid rgba(255, 213, 79, 0.2)',
                borderRadius: '14px',
                overflow: 'hidden',
              }}
            >
              {/* Header - clickable */}
              <button
                onClick={() => setExpanded(isExpanded ? null : msg.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: '24px' }}>{msg.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{
                    color: isRead ? 'rgba(255,255,255,0.7)' : '#e8eaf6',
                    fontSize: '15px',
                    fontWeight: isRead ? 500 : 700,
                  }}>
                    {msg.title}
                  </div>
                  <div style={{
                    color: 'rgba(255,255,255,0.4)',
                    fontSize: '12px',
                    marginTop: '2px',
                  }}>
                    {formatDate(msg.created_at)}
                  </div>
                </div>
                {isRead
                  ? <MailOpen size={18} style={{ color: 'rgba(255,255,255,0.3)' }} />
                  : <Mail size={18} style={{ color: '#ffd54f' }} />
                }
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div style={{
                  padding: '0 16px 16px',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                }}>
                  <div style={{
                    color: '#e8eaf6',
                    fontSize: '14px',
                    lineHeight: 1.7,
                    paddingTop: '12px',
                  }}
                    dangerouslySetInnerHTML={{
                      __html: msg.content
                        .replace(/\n/g, '<br/>')
                        .replace(/\*\*([^*]+)\*\*/g, '<strong style="color: #ffd54f">$1</strong>')
                        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #ffd54f; text-decoration: underline; font-weight: 600">$1</a>')
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
